import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api, formatEUR, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import {
    Users, Receipt, CurrencyEur, ChartLineUp, Download, Crown, FileArrowDown, Key, Copy, Envelope, FileText, Lightning, PaperPlaneTilt, EnvelopeSimple, IdentificationCard, WhatsappLogo, Buildings,
} from "@phosphor-icons/react";
import ProspectosTab from "@/pages/admin/ProspectosTab";

const TABS = [
    { id: "overview", label: "Resumen", icon: Crown },
    { id: "prospectos", label: "Prospectos", icon: IdentificationCard },
    { id: "users", label: "Usuarios", icon: Users },
    { id: "leads", label: "Leads", icon: Envelope },
    { id: "asesorias", label: "Asesorías", icon: Buildings },
    { id: "campaigns", label: "Campañas", icon: PaperPlaneTilt },
    { id: "analyses", label: "Análisis", icon: FileText },
    { id: "invoices", label: "Facturación", icon: Receipt },
];

export default function AdminDashboard() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState("overview");
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [leads, setLeads] = useState([]);
    const [analyses, setAnalyses] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [dripRunResult, setDripRunResult] = useState(null);
    const [dripRunning, setDripRunning] = useState(false);
    const [sendingManual, setSendingManual] = useState(null); // lead being sent
    const [quarter, setQuarter] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [resetResult, setResetResult] = useState(null);
    const [adjustingUser, setAdjustingUser] = useState(null);

    async function load() {
        setLoading(true);
        // Cada llamada falla por separado — si un endpoint no existe aún (p.ej. tras redeploy parcial),
        // el resto del panel sigue funcionando.
        const safeGet = async (url, setter, fallback = []) => {
            try {
                const { data } = await api.get(url);
                setter(Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : fallback));
            } catch (e) {
                console.warn(`[admin] ${url} falló:`, e?.response?.status, e?.response?.data);
                setter(fallback);
            }
        };
        try {
            await Promise.all([
                api.get("/admin/stats").then(r => setStats(r.data)).catch(e => {
                    console.warn("[admin] /admin/stats falló:", e?.response?.status);
                    setStats({
                        users: { total: 0, by_plan: { pro: 0, empresa: 0, free: 0 } },
                        invoices: { total: 0, this_month: 0 },
                        revenue: { total_eur: 0, this_month_eur: 0 },
                        analyses: { total: 0, this_month: 0, with_interpretation: 0 },
                        leads: { total: 0, this_month: 0 },
                        mrr_eur: 0,
                    });
                }),
                safeGet("/admin/users", setUsers),
                safeGet("/admin/invoices", setInvoices),
                safeGet("/admin/leads", setLeads),
                safeGet("/admin/analyses", setAnalyses),
                safeGet("/admin/templates", setTemplates),
            ]);
        } catch (e) {
            setError(formatApiError(e, "Error al cargar datos admin"));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    function downloadCsv(extraQuery = "") {
        const token = localStorage.getItem("gf_token");
        const q = extraQuery ? `?${extraQuery}` : "";
        const url = `${API}/admin/invoices/export.csv${q}`;
        // Open in new tab with token as Bearer (via fetch then blob download)
        fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
            .then(r => r.blob())
            .then(blob => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `libro_facturas${extraQuery ? "_" + extraQuery.split("=")[1] : ""}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((err) => setError(err.message));
    }

    function downloadLeadsCsv() {
        const token = localStorage.getItem("gf_token");
        fetch(`${API}/admin/leads/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch((err) => setError(err.message));
    }

    async function runDrip() {
        if (!confirm("Esto enviará los emails de la secuencia automática a todos los leads que toque hoy. ¿Continuar?")) return;
        setDripRunning(true); setError("");
        try {
            const { data } = await api.post("/admin/drip/run");
            setDripRunResult(data);
        } catch (e) {
            setError(formatApiError(e, "Error al ejecutar el drip"));
        } finally {
            setDripRunning(false);
        }
    }

    async function sendManualToLead(lead, templateId) {
        setSendingManual(lead.id); setError("");
        try {
            await api.post(`/admin/leads/${lead.id}/send-manual`, { template_id: templateId });
            alert(`Email "${templates.find(t => t.id === templateId)?.subject || "manual"}" enviado a ${lead.email}`);
        } catch (e) {
            setError(formatApiError(e, "Error al enviar"));
        } finally {
            setSendingManual(null);
        }
    }

    async function updateTemplate(templateId, fields) {
        setError("");
        try {
            await api.put(`/admin/templates/${templateId}`, fields);
            const tpl = await api.get("/admin/templates");
            setTemplates(tpl.data.items || []);
            return true;
        } catch (e) {
            setError(formatApiError(e, "Error al guardar"));
            return false;
        }
    }

    function openWhatsAppForLead(lead) {
        // Normaliza el teléfono al formato wa.me (solo dígitos con prefijo país)
        const raw = (lead.telefono || "").replace(/\D/g, "");
        let digits = raw;
        if (digits.length === 9) digits = `34${digits}`;       // ES sin prefijo
        else if (digits.length === 11 && digits.startsWith("34")) {/* ok */}
        else if (digits.length < 9) {
            alert(`Teléfono inválido para WhatsApp: "${lead.telefono || "(vacío)"}"`);
            return;
        }

        // Plantilla del mensaje según el tipo de cliente
        const empresa = lead.nombre_empresa ? ` para ${lead.nombre_empresa}` : "";
        const saludo = lead.nombre ? `Hola ${lead.nombre.split(" ")[0]}` : "Hola";
        let body;
        if (lead.tipo_cliente === "asesoria") {
            body = `${saludo}, te escribo desde *Gestión Fiscal 347*. He visto que has probado *La Máquina de la Verdad*${empresa}.

Trabajamos con asesorías como la tuya preparando *expedientes pre-bancarios* — ese trabajo de interpretar el balance que tu cliente te pide cuando va a financiación y que un analista detecta en segundos.

Cada Diagnóstico Pre-Bancario te cuesta *19€ + IVA (22,99€)*. Tú lo facturas a tu cliente al precio que decidas (60, 90, 120€). Sin packs, sin suscripciones, sin caducidad.

¿Te llamo 10 minutos esta semana? Más de 30 años en banca empresarial. Un saludo.`;
        } else {
            body = `${saludo}, te escribo desde *Gestión Fiscal 347*. He visto que has hecho un diagnóstico de tu balance${empresa} con *La Máquina de la Verdad*.

Hemos detectado puntos que el banco va a marcar. ¿Te parece si te explico en 10 min, sin compromiso, cómo podrías corregirlos antes de presentar el expediente?

Más de 30 años en banca empresarial. Un saludo.`;
        }

        const url = `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }

    if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

    async function handleResetPassword(targetUser) {
        if (!confirm(`¿Generar nueva contraseña temporal para ${targetUser.email}?\n\nLa contraseña actual dejará de funcionar.`)) return;
        try {
            const { data } = await api.post(`/admin/users/${targetUser.id}/reset-password`);
            setResetResult(data);
        } catch (e) {
            setError(formatApiError(e, "Error al resetear contraseña"));
        }
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text);
    }

    async function sendOutreachToAsesorias() {
        if (!confirm("Vas a enviar el email de presentación a TODAS las asesorías en estado 'sin_contactar'. ¿Continuar?")) return;
        try {
            const { data } = await api.post("/admin/prospectos/bulk-send", {
                template_id: "outreach_asesorias",
                target: "sin_contactar",
                tipo: "asesoria",
            });
            if (data.queued) {
                alert(`📨 Campaña encolada\n\nDestinatarios: ${data.total_objetivo}\nTiempo estimado: ≈ ${data.eta_minutos} min\n\n${data.mensaje}`);
            } else {
                alert(`Campaña: ${data.mensaje || "—"}\n\nObjetivo: ${data.total_objetivo}\nEnviados: ${data.enviados}\nFallidos: ${data.fallidos}`);
            }
        } catch (e) {
            setError(formatApiError(e, "Error al enviar campaña"));
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-12">
                <div className="flex flex-col md:flex-row justify-between gap-6 mb-10 pb-8 border-b border-[var(--gf-border)]">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <Crown size={20} weight="fill" className="text-[var(--gf-gold)]" />
                            <span className="micro-label">— Panel administrativo</span>
                        </div>
                        <h1 className="serif text-4xl md:text-5xl">Control General</h1>
                        <p className="text-[var(--gf-text-secondary)] mt-2">
                            Visión global de tu negocio: usuarios, facturación y exportación AEAT.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-6">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-20">
                        <span className="elegant-loader"><span /><span /><span /></span>
                    </div>
                ) : stats ? (
                    <>
                        {/* KPIs */}
                        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                            <Kpi icon={<Users size={22} weight="light" />} label="Usuarios totales" value={stats.users.total} subtitle={`Pro: ${stats.users.by_plan.pro} · Asesoría: ${stats.users.by_plan.empresa} · Free: ${stats.users.by_plan.free}`} testId="kpi-users" />
                            <Kpi icon={<CurrencyEur size={22} weight="light" />} label="MRR (recurrente)" value={formatEUR(stats.mrr_eur)} subtitle="Ingreso mensual proyectado" highlight testId="kpi-mrr" />
                            <Kpi icon={<Receipt size={22} weight="light" />} label="Ingresos este mes" value={formatEUR(stats.revenue.this_month_eur)} subtitle={`Total histórico: ${formatEUR(stats.revenue.total_eur)}`} testId="kpi-revenue-month" />
                            <Kpi icon={<ChartLineUp size={22} weight="light" />} label="Análisis realizados" value={stats.analyses.total} subtitle={`${stats.analyses.with_interpretation} con IA · ${stats.analyses.this_month} este mes`} testId="kpi-analyses" />
                            <Kpi icon={<Envelope size={22} weight="light" />} label="Leads captados" value={stats.leads?.total || 0} subtitle={`${stats.leads?.this_month || 0} este mes · La Máquina`} testId="kpi-leads" />
                            {/* Conversión bloqueo → CTA (calculado client-side desde leads) */}
                            {(() => {
                                const clicked = leads.filter((l) => l.upgrade_clicked_at).length;
                                const total = leads.length;
                                const pct = total > 0 ? Math.round((clicked / total) * 100) : 0;
                                return (
                                    <Kpi
                                        icon={<Lightning size={22} weight="light" />}
                                        label="Conv. → CTA"
                                        value={total > 0 ? `${pct}%` : "—"}
                                        subtitle={`${clicked} de ${total} leads pulsaron Contratar`}
                                        testId="kpi-cta-conversion"
                                    />
                                );
                            })()}
                        </div>

                        {/* Tabs */}
                        <div className="border-b border-[var(--gf-border)] mb-8 overflow-x-auto" data-testid="admin-tabs">
                            <div className="flex gap-1">
                                {TABS.map((t) => {
                                    const IconEl = t.icon;
                                    const active = activeTab === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => setActiveTab(t.id)}
                                            className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${active ? "border-[var(--gf-gold)] text-[var(--gf-navy)] font-semibold" : "border-transparent text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)]"}`}
                                            data-testid={`admin-tab-${t.id}`}
                                        >
                                            <IconEl size={16} weight={active ? "fill" : "light"} className={active ? "text-[var(--gf-gold)]" : ""} />
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* === TAB: OVERVIEW (Exportación AEAT) === */}
                        {activeTab === "overview" && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="gf-card mb-12">
                            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
                                <div>
                                    <div className="micro-label mb-2">— Exportación AEAT</div>
                                    <h2 className="serif text-2xl mb-2">Libro registro de facturas emitidas</h2>
                                    <p className="text-sm text-[var(--gf-text-secondary)]">
                                        Descarga en CSV compatible con modelos 303/390 y hojas de cálculo (Excel).
                                        Formato: <span className="mono">Número ; Fecha ; NIF ; Razón social ; Concepto ; Base ; IVA ; Cuota ; Total ; Estado</span>
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <select value={quarter} onChange={(e) => setQuarter(e.target.value)} className="gf-input mono" style={{ width: "auto" }} data-testid="quarter-select">
                                        <option value="">Todo el historial</option>
                                        <option value={`${new Date().getFullYear()}-T1`}>{new Date().getFullYear()} · T1 (Ene-Mar)</option>
                                        <option value={`${new Date().getFullYear()}-T2`}>{new Date().getFullYear()} · T2 (Abr-Jun)</option>
                                        <option value={`${new Date().getFullYear()}-T3`}>{new Date().getFullYear()} · T3 (Jul-Sep)</option>
                                        <option value={`${new Date().getFullYear()}-T4`}>{new Date().getFullYear()} · T4 (Oct-Dic)</option>
                                        <option value={`${new Date().getFullYear() - 1}-T4`}>{new Date().getFullYear() - 1} · T4</option>
                                    </select>
                                    <button onClick={() => downloadCsv(quarter ? `quarter=${quarter}` : "")} className="btn-gold" data-testid="export-csv-btn">
                                        <FileArrowDown size={16} weight="bold" /> Descargar CSV
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                        )}

                        {/* === TAB: PROSPECTOS === */}
                        {activeTab === "prospectos" && (
                            <ProspectosTab templates={templates} />
                        )}

                        {/* === TAB: USERS === */}
                        {activeTab === "users" && (
                        <div className="gf-card mb-10">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="micro-label mb-2">— Usuarios</div>
                                    <h2 className="serif text-2xl">Últimos registrados ({users.length})</h2>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="data-table" data-testid="admin-users-table">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Nombre</th>
                                            <th>Plan</th>
                                            <th className="text-right">Créditos</th>
                                            <th className="text-right">Tipo</th>
                                            <th className="text-right">Informes/mes</th>
                                            <th>Registro</th>
                                            <th className="text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.slice(0, 20).map((u) => (
                                            <tr key={u.id}>
                                                <td className="text-sm">{u.email}{u.role === "admin" && <span className="text-[10px] ml-2 bg-[var(--gf-gold)] text-white px-1.5 py-0.5 uppercase tracking-widest">ADMIN</span>}</td>
                                                <td className="text-sm text-[var(--gf-text-secondary)]">{u.name}</td>
                                                <td><span className={`text-xs uppercase tracking-widest px-2 py-0.5 ${u.plan === "empresa" ? "bg-[var(--gf-navy)] text-white" : u.plan === "pro" ? "bg-[var(--gf-gold)] text-white" : "bg-[var(--gf-subtle)] text-[var(--gf-text-secondary)]"}`}>{u.plan === "empresa" ? "Asesoría" : u.plan}</span></td>
                                                <td className="text-right mono text-sm">{formatEUR(u.credits_eur || 0)}</td>
                                                <td className="text-right mono text-sm">{u.tipo_cliente === "asesoria" ? "Asesoría" : "-"}</td>
                                                <td className="text-right mono text-sm">{u.reports_this_month || 0}</td>
                                                <td className="text-sm text-[var(--gf-text-secondary)]">{new Date(u.created_at).toLocaleDateString("es-ES")}</td>
                                                <td className="text-right">
                                                    <div className="inline-flex items-center gap-3">
                                                        <button onClick={() => setAdjustingUser(u)} className="text-[var(--gf-navy)] hover:text-[var(--gf-gold)] inline-flex items-center gap-1 text-xs" data-testid={`adjust-user-${u.id}`}>
                                                            <CurrencyEur size={13} /> Ajustar
                                                        </button>
                                                        {u.role !== "admin" && (
                                                            <button onClick={() => handleResetPassword(u)} className="text-[var(--gf-navy)] hover:text-[var(--gf-gold)] inline-flex items-center gap-1 text-xs" data-testid={`reset-pw-${u.id}`}>
                                                                <Key size={13} /> Reset pass
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        )}

                        {/* === TAB: LEADS === */}
                        {activeTab === "leads" && (
                        <div className="gf-card mb-10">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="micro-label mb-2">— Leads de La Máquina de la Verdad</div>
                                    <h2 className="serif text-2xl">Capturados ({leads.length})</h2>
                                    <p className="text-sm text-[var(--gf-text-secondary)] mt-1">
                                        Visitantes que han probado el diagnóstico y han dejado su email para recibirlo.
                                    </p>
                                </div>
                                <button onClick={downloadLeadsCsv} className="btn-gold text-sm" data-testid="export-leads-csv">
                                    <FileArrowDown size={14} weight="bold" /> Exportar CSV
                                </button>
                            </div>
                            {leads.length === 0 ? (
                                <div className="text-center py-10 text-[var(--gf-text-secondary)] text-sm italic">
                                    Aún no se ha capturado ningún lead. Compártelo en redes para empezar a recibirlos.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="data-table" data-testid="admin-leads-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Contacto</th>
                                                <th>Empresa</th>
                                                <th>Tipo</th>
                                                <th>Veredicto</th>
                                                <th className="text-right">Alertas</th>
                                                <th className="text-right">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leads.map((l) => (
                                                <tr key={l.id}>
                                                    <td className="text-sm text-[var(--gf-text-secondary)]">
                                                        {new Date(l.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                                    </td>
                                                    <td className="text-sm">
                                                        <a href={`mailto:${l.email}`} className="hover:text-[var(--gf-gold)] underline-offset-4 hover:underline">{l.email}</a>
                                                        {l.telefono && (
                                                            <div className="text-[11px] text-[var(--gf-text-muted)] mono mt-0.5">{l.telefono}</div>
                                                        )}
                                                        {l.upgrade_clicked_at && (
                                                            <div className="inline-block mt-1 px-1.5 py-0.5 bg-[var(--gf-gold)]/15 text-[var(--gf-gold)] text-[9px] tracking-widest uppercase font-semibold" title={`Pulsó "Contratar" el ${new Date(l.upgrade_clicked_at).toLocaleString("es-ES")}`}>
                                                                🔥 Pulsó CTA
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-sm text-[var(--gf-text-secondary)]">
                                                        {l.nombre_empresa || "—"}
                                                        {l.nombre && (
                                                            <div className="text-[11px] text-[var(--gf-text-muted)] mt-0.5">{l.nombre}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {l.tipo_cliente ? (
                                                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold ${l.tipo_cliente === "asesoria" ? "bg-[var(--gf-gold)]/15 text-[var(--gf-gold)]" : "bg-[var(--gf-navy)]/10 text-[var(--gf-navy)]"}`}>
                                                                {l.tipo_cliente === "asesoria" ? "Asesoría" : "Empresa"}
                                                            </span>
                                                        ) : (
                                                            <span className="text-[10px] text-[var(--gf-text-muted)]">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {l.veredicto && (
                                                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold ${l.veredicto === "verde" ? "bg-green-50 text-[var(--gf-success)]" : l.veredicto === "ambar" ? "bg-amber-50 text-[var(--gf-warning)]" : "bg-red-50 text-[var(--gf-danger)]"}`}>
                                                                {l.veredicto}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-right mono text-xs">
                                                        {l.rojos > 0 && <span className="text-[var(--gf-danger)] font-semibold">{l.rojos}🔴</span>}
                                                        {l.ambars > 0 && <span className="text-[var(--gf-warning)] ml-2">{l.ambars}🟡</span>}
                                                    </td>
                                                    <td className="text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={() => openWhatsAppForLead(l)}
                                                                disabled={!l.telefono}
                                                                title={l.telefono ? `Abrir WhatsApp para ${l.telefono}` : "Sin teléfono"}
                                                                className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#25D366] text-white hover:bg-[#1ea953] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                                data-testid={`lead-wa-${l.id}`}
                                                            >
                                                                <WhatsappLogo size={14} weight="bold" /> WhatsApp
                                                            </button>
                                                            {l.unsubscribed ? (
                                                                <span className="text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)]">Baja</span>
                                                            ) : (
                                                                <select
                                                                    onChange={(e) => { if (e.target.value) { sendManualToLead(l, e.target.value); e.target.value = ""; } }}
                                                                    disabled={sendingManual === l.id}
                                                                    defaultValue=""
                                                                    className="text-xs border border-[var(--gf-border)] bg-white px-2 py-1"
                                                                    data-testid={`lead-send-${l.id}`}
                                                                >
                                                                    <option value="" disabled>{sendingManual === l.id ? "Enviando…" : "Email…"}</option>
                                                                    {templates.filter(t => t.active).map(t => (
                                                                        <option key={t.id} value={t.id}>{t.subject.slice(0, 40)}</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        )}

                        {/* === TAB: ASESORÍAS === */}
                        {activeTab === "asesorias" && (
                        <AsesoriasTab
                            leads={leads}
                            users={users}
                            onWhatsApp={openWhatsAppForLead}
                            templates={templates}
                            onSendManual={sendManualToLead}
                            sendingManual={sendingManual}
                            onSendCampaign={sendOutreachToAsesorias}
                        />
                        )}

                        {/* === TAB: CAMPAIGNS === */}
                        {activeTab === "campaigns" && (
                        <div className="space-y-6 mb-10">
                            {/* Drip header + run button */}
                            <div className="gf-card border-t-4 border-t-[var(--gf-gold)]">
                                <div className="flex items-start gap-4 justify-between flex-wrap">
                                    <div className="flex items-start gap-3">
                                        <PaperPlaneTilt size={28} weight="light" className="text-[var(--gf-gold)] mt-1" />
                                        <div>
                                            <div className="micro-label">— Drip automático</div>
                                            <h2 className="serif text-2xl mt-1">Secuencia de emails para leads</h2>
                                            <p className="text-sm text-[var(--gf-text-secondary)] mt-2 max-w-xl leading-relaxed">
                                                Cada lead que prueba La Máquina y deja su email recibe automáticamente esta secuencia.
                                                Edita el asunto o el cuerpo de cada plantilla y pulsa <strong>"Procesar drip ahora"</strong>
                                                para enviar lo pendiente. Los emails ya enviados no se repiten.
                                            </p>
                                        </div>
                                    </div>
                                    <button onClick={runDrip} disabled={dripRunning} className="btn-gold whitespace-nowrap" data-testid="run-drip-btn">
                                        <PaperPlaneTilt size={16} weight="fill" /> {dripRunning ? "Procesando…" : "Procesar drip ahora"}
                                    </button>
                                </div>
                                {dripRunResult && (
                                    <div className="mt-4 border-l-2 border-[var(--gf-success)] bg-green-50/60 pl-4 py-2 text-sm" data-testid="drip-run-result">
                                        Procesado: <strong>{dripRunResult.processed}</strong> emails de drip · enviados correctamente <strong>{dripRunResult.sent}</strong> · revisados {dripRunResult.leads_checked} leads.
                                        {dripRunResult.errors?.length > 0 && (
                                            <div className="text-[var(--gf-danger)] mt-2 text-xs">Errores: {dripRunResult.errors.join(" · ")}</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Campaña Sociedades — envío masivo a prospectos */}
                            <BulkCampaignBox templates={templates} onSent={load} />

                            {/* Templates editor */}
                            {templates.map((t) => (
                                <TemplateCard key={t.id} template={t} onSave={(fields) => updateTemplate(t.id, fields)} />
                            ))}
                        </div>
                        )}

                        {/* === TAB: ANALYSES === */}
                        {activeTab === "analyses" && (
                        <div className="gf-card mb-10">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="micro-label mb-2">— Análisis realizados</div>
                                    <h2 className="serif text-2xl">Últimos análisis ({analyses.length})</h2>
                                    <p className="text-sm text-[var(--gf-text-secondary)] mt-1">
                                        Histórico de balances analizados por todos los usuarios.
                                    </p>
                                </div>
                            </div>
                            {analyses.length === 0 ? (
                                <div className="text-center py-10 text-[var(--gf-text-secondary)] text-sm italic">
                                    Aún no se ha realizado ningún análisis.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="data-table" data-testid="admin-analyses-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha</th>
                                                <th>Empresa</th>
                                                <th>Periodo</th>
                                                <th>Usuario</th>
                                                <th>IA</th>
                                                <th>Master</th>
                                                <th className="text-right">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analyses.slice(0, 50).map((a) => (
                                                <tr key={a.id}>
                                                    <td className="text-sm text-[var(--gf-text-secondary)]">
                                                        {new Date(a.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "2-digit" })}
                                                    </td>
                                                    <td className="text-sm">{a.company_name || "—"}</td>
                                                    <td className="text-sm mono">{a.period || "—"}</td>
                                                    <td className="text-xs text-[var(--gf-text-secondary)]">{a.user_email || "—"}</td>
                                                    <td>
                                                        {a.has_interpretation ? (
                                                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[var(--gf-gold)] text-white">Sí</span>
                                                        ) : (
                                                            <span className="text-[10px] text-[var(--gf-text-muted)]">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {a.is_master ? (
                                                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[var(--gf-navy)] text-white">Master</span>
                                                        ) : (
                                                            <span className="text-[10px] text-[var(--gf-text-muted)]">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-right">
                                                        <a href={`/analisis/${a.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[var(--gf-navy)] hover:text-[var(--gf-gold)] underline underline-offset-4" data-testid={`view-analysis-${a.id}`}>
                                                            Abrir →
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        )}

                        {/* === TAB: INVOICES === */}
                        {activeTab === "invoices" && (
                        <div className="gf-card">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <div className="micro-label mb-2">— Facturas emitidas</div>
                                    <h2 className="serif text-2xl">Últimas facturas ({invoices.length})</h2>
                                </div>
                                <button onClick={() => downloadCsv("")} className="btn-outline text-sm" data-testid="export-all-csv">
                                    <Download size={14} /> Todas (CSV)
                                </button>
                            </div>
                            {invoices.length === 0 ? (
                                <div className="text-center py-10 text-[var(--gf-text-secondary)]">
                                    Aún no se ha emitido ninguna factura.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="data-table" data-testid="admin-invoices-table">
                                        <thead>
                                            <tr>
                                                <th>Nº</th>
                                                <th>Fecha</th>
                                                <th>Cliente</th>
                                                <th>Concepto</th>
                                                <th className="text-right">Base</th>
                                                <th className="text-right">IVA</th>
                                                <th className="text-right">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.slice(0, 50).map((inv) => (
                                                <tr key={inv.id}>
                                                    <td className="mono text-sm">{inv.number}</td>
                                                    <td className="text-sm text-[var(--gf-text-secondary)]">
                                                        {new Date(inv.date).toLocaleDateString("es-ES")}
                                                    </td>
                                                    <td className="text-sm">{inv.fiscal_profile?.razon_social || inv.user_email}</td>
                                                    <td className="text-sm">{inv.concept}</td>
                                                    <td className="text-right mono text-sm">{formatEUR(inv.base, 2)}</td>
                                                    <td className="text-right mono text-sm">{formatEUR(inv.iva_amount, 2)}</td>
                                                    <td className="text-right mono font-semibold">{formatEUR(inv.total, 2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        )}
                    </>
                ) : null}
            </main>

            {resetResult && (
                <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-6" data-testid="reset-result-modal">
                    <div className="bg-white max-w-md w-full border border-[var(--gf-border)] p-8">
                        <div className="flex items-center gap-2 mb-4">
                            <Key size={22} weight="light" className="text-[var(--gf-gold)]" />
                            <span className="micro-label">Contraseña temporal generada</span>
                        </div>
                        <h2 className="serif text-2xl mb-2">{resetResult.user_email}</h2>
                        <p className="text-sm text-[var(--gf-text-secondary)] mb-6">
                            Comparte esta contraseña con el usuario por WhatsApp o email. Es de un solo uso: debería cambiarla al entrar.
                        </p>
                        <div className="bg-[var(--gf-navy)] text-white p-6 text-center mb-6">
                            <div className="micro-label text-[var(--gf-gold)] mb-2">Contraseña temporal</div>
                            <div className="mono text-3xl tracking-widest font-semibold" data-testid="temp-password">{resetResult.temp_password}</div>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => copyToClipboard(resetResult.temp_password)} className="btn-outline flex-1 justify-center" data-testid="copy-temp-pw">
                                <Copy size={16} /> Copiar
                            </button>
                            <button onClick={() => setResetResult(null)} className="btn-primary flex-1 justify-center" data-testid="close-reset-modal">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
            <WhatsAppFab />
            {adjustingUser && (
                <AdjustUserModal user={adjustingUser} onClose={() => setAdjustingUser(null)} onSaved={() => { setAdjustingUser(null); load(); }} />
            )}
        </div>
    );
}

function AdjustUserModal({ user, onClose, onSaved }) {
    const [tab, setTab] = useState("grant"); // grant | manual
    const [planId, setPlanId] = useState("bono50");
    const [addBono, setAddBono] = useState("");
    const [addMaster, setAddMaster] = useState("");
    const [planAssign, setPlanAssign] = useState("");
    const [tipoCliente, setTipoCliente] = useState(user.tipo_cliente || "empresa");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");

    async function applyGrant() {
        if (!planId) return;
        setErr(""); setSubmitting(true);
        try {
            const { data } = await api.post(`/admin/users/${user.id}/grant-from-payment`, { plan_id: planId, note });
            setResult({ type: "grant", data });
        } catch (e) {
            setErr(formatApiError(e, "No se pudo aplicar el plan"));
        } finally {
            setSubmitting(false);
        }
    }
    async function applyManual() {
        const body = { note };
        if (addBono) body.add_credits_eur = parseFloat(addBono);
        if (planAssign) body.plan = planAssign;
        if (tipoCliente && tipoCliente !== (user.tipo_cliente || "empresa")) body.tipo_cliente = tipoCliente;
        if (Object.keys(body).length === 1) { setErr("Indica al menos un cambio"); return; }
        setErr(""); setSubmitting(true);
        try {
            const { data } = await api.post(`/admin/users/${user.id}/adjust`, body);
            setResult({ type: "manual", data });
        } catch (e) {
            setErr(formatApiError(e, "No se pudo aplicar el ajuste"));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="adjust-modal">
            <div className="bg-white max-w-lg w-full border border-[var(--gf-border)] max-h-[90vh] overflow-y-auto">
                <div className="bg-[var(--gf-navy)] text-white p-6">
                    <div className="micro-label text-[var(--gf-gold)] mb-2">Gestión de cuenta</div>
                    <h2 className="serif text-2xl">{user.email}</h2>
                    <div className="text-xs text-white/70 mt-1">
                        Plan: {user.plan || "free"} · Créditos: {formatEUR(user.credits_eur || 0)}
                    </div>
                </div>

                <div className="flex border-b border-[var(--gf-border)]">
                    <button onClick={() => setTab("grant")} className={`flex-1 px-4 py-3 text-sm tracking-wide ${tab === "grant" ? "border-b-2 border-[var(--gf-gold)]" : "text-[var(--gf-text-secondary)]"}`} data-testid="tab-grant">Aplicar plan pagado</button>
                    <button onClick={() => setTab("manual")} className={`flex-1 px-4 py-3 text-sm tracking-wide ${tab === "manual" ? "border-b-2 border-[var(--gf-gold)]" : "text-[var(--gf-text-secondary)]"}`} data-testid="tab-manual">Ajuste manual</button>
                </div>

                <div className="p-6">
                    {result ? (
                        <div className="text-center py-4">
                            <div className="text-3xl mb-3">✅</div>
                            <h3 className="serif text-xl mb-2">Cambios aplicados</h3>
                            {result.type === "grant" && (
                                <p className="text-sm text-[var(--gf-text-secondary)] mb-4">
                                    Plan <strong>{result.data?.plan_applied}</strong> activado. Factura {result.data?.invoice_number} generada.
                                </p>
                            )}
                            {result.type === "manual" && (
                                <p className="text-sm text-[var(--gf-text-secondary)] mb-4">
                                    Ajuste registrado y guardado en auditoría.
                                </p>
                            )}
                            <div className="flex gap-3 justify-center">
                                <button onClick={onSaved} className="btn-primary" data-testid="adjust-done-btn">Hecho</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {tab === "grant" && (
                                <div className="space-y-4">
                                    <p className="text-sm text-[var(--gf-text-secondary)]">
                                        Aplica un plan como si el usuario hubiera completado el pago. Útil para regularizar pagos cuya confirmación falló (webhook caído, transferencia bancaria…). Se generará la factura correspondiente.
                                    </p>
                                    <div>
                                        <label className="gf-label">Plan a aplicar</label>
                                        <select value={planId} onChange={(e) => setPlanId(e.target.value)} className="gf-input" data-testid="grant-plan-select">
                                            <option value="diagnostico">Diagnóstico Pre-Bancario — 19€ + IVA (22,99€) · 1 informe</option>
                                            <option value="master">Master Pre-Bancario — 39€ + IVA (47,19€) · 1 informe master</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="gf-label">Nota (opcional)</label>
                                        <input value={note} onChange={(e) => setNote(e.target.value)} className="gf-input" placeholder="Ej: pago Stripe 11/05/2026 - session cs_test_..." data-testid="grant-note" />
                                    </div>
                                </div>
                            )}
                            {tab === "manual" && (
                                <div className="space-y-4">
                                    <p className="text-sm text-[var(--gf-text-secondary)]">
                                        Modifica créditos directamente. NO genera factura.
                                    </p>
                                    <div>
                                        <label className="gf-label">Sumar / restar créditos (€)</label>
                                        <input type="number" step="0.01" value={addBono} onChange={(e) => setAddBono(e.target.value)} className="gf-input" placeholder="+100 o -50" data-testid="adjust-bono" />
                                    </div>
                                    <div>
                                        <label className="gf-label">Cambiar plan (opcional)</label>
                                        <select value={planAssign} onChange={(e) => setPlanAssign(e.target.value)} className="gf-input" data-testid="adjust-plan">
                                            <option value="">— No cambiar plan —</option>
                                            <option value="free">Free</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="gf-label">Tipo de cliente</label>
                                        <select value={tipoCliente} onChange={(e) => setTipoCliente(e.target.value)} className="gf-input" data-testid="adjust-tipo-cliente">
                                            <option value="empresa">Empresa</option>
                                            <option value="autonomo">Autónomo</option>
                                            <option value="asesoria">Asesoría</option>
                                        </select>
                                        <p className="text-xs text-[var(--gf-text-muted)] mt-1">Actual: {user.tipo_cliente || "empresa"}</p>
                                    </div>
                                    <div>
                                        <label className="gf-label">Nota administrativa</label>
                                        <input value={note} onChange={(e) => setNote(e.target.value)} className="gf-input" placeholder="Motivo del ajuste" data-testid="adjust-note" />
                                    </div>
                                </div>
                            )}

                            {err && <div className="mt-4 p-3 bg-red-50 border-l-2 border-red-500 text-sm text-red-700">{err}</div>}

                            <div className="flex gap-3 mt-6">
                                <button onClick={onClose} className="btn-outline flex-1 justify-center" data-testid="adjust-cancel">Cancelar</button>
                                <button onClick={tab === "grant" ? applyGrant : applyManual} disabled={submitting} className="btn-primary flex-1 justify-center" data-testid="adjust-apply">
                                    {submitting ? "Aplicando..." : "Aplicar"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Kpi({ icon, label, value, subtitle, highlight, testId }) {
    return (
        <div className={`gf-card-ficha ${highlight ? "border-t-2 border-t-[var(--gf-gold)]" : ""}`} data-testid={testId}>
            <div className="flex items-center justify-between mb-4">
                <span className="text-[var(--gf-navy)]">{icon}</span>
                <span className="micro-label">{label}</span>
            </div>
            <div className="editorial-num mono" style={{ fontSize: "2.5rem" }}>{value}</div>
            {subtitle && <div className="text-xs text-[var(--gf-text-muted)] mt-2 leading-snug">{subtitle}</div>}
        </div>
    );
}

function TemplateCard({ template, onSave }) {
    const [subject, setSubject] = useState(template.subject);
    const [htmlBody, setHtmlBody] = useState(template.html_body);
    const [dayOffset, setDayOffset] = useState(template.day_offset);
    const [active, setActive] = useState(template.active !== false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [preview, setPreview] = useState(false);

    async function save() {
        setSaving(true); setSaved(false);
        const ok = await onSave({ subject, html_body: htmlBody, day_offset: Number(dayOffset), active });
        setSaving(false);
        if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    }

    return (
        <div className="gf-card" data-testid={`template-${template.id}`}>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="text-[10px] tracking-widest uppercase text-[var(--gf-gold)] font-semibold">
                        Plantilla · D+{template.day_offset}
                    </div>
                    <div className="text-sm mono text-[var(--gf-text-muted)] mt-0.5">{template.id}</div>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid={`template-active-${template.id}`} />
                    <span className={active ? "text-[var(--gf-success)] font-semibold" : "text-[var(--gf-text-muted)]"}>{active ? "Activa" : "Pausada"}</span>
                </label>
            </div>

            <div className="grid md:grid-cols-[1fr_120px] gap-3 mb-3">
                <div>
                    <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Asunto del email</label>
                    <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className="gf-input text-sm" data-testid={`template-subject-${template.id}`} />
                </div>
                <div>
                    <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Día (offset)</label>
                    <input type="number" value={dayOffset} onChange={(e) => setDayOffset(e.target.value)} min="0" max="365" className="gf-input mono text-sm" data-testid={`template-day-${template.id}`} />
                </div>
            </div>

            <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)]">Cuerpo HTML del email</label>
                    <button type="button" onClick={() => setPreview(!preview)} className="text-[10px] underline text-[var(--gf-gold)]" data-testid={`template-preview-${template.id}`}>
                        {preview ? "Editar" : "Vista previa"}
                    </button>
                </div>
                {preview ? (
                    <div className="border border-[var(--gf-border)] p-4 bg-white" dangerouslySetInnerHTML={{ __html: htmlBody.replace(/\{nombre\}/g, " en <strong>Tu Empresa</strong>").replace(/\{app_url\}/g, "https://gf347.es") }} />
                ) : (
                    <textarea value={htmlBody} onChange={(e) => setHtmlBody(e.target.value)} rows={12} className="gf-input mono text-xs w-full" style={{ fontFamily: "monospace" }} data-testid={`template-body-${template.id}`} />
                )}
                <p className="text-[10px] text-[var(--gf-text-muted)] mt-1">
                    Variables disponibles: <code className="mono bg-[var(--gf-subtle)] px-1">{"{nombre}"}</code> (empresa) ·
                    <code className="mono bg-[var(--gf-subtle)] px-1 ml-1">{"{app_url}"}</code> (URL base).
                    Pie de baja se añade automáticamente.
                </p>
            </div>

            <div className="flex items-center gap-3 justify-end">
                {saved && <span className="text-xs text-[var(--gf-success)]">✓ Guardado</span>}
                <button onClick={save} disabled={saving} className="btn-gold text-sm" data-testid={`template-save-${template.id}`}>
                    {saving ? "Guardando…" : "Guardar cambios"}
                </button>
            </div>
        </div>
    );
}

function BulkCampaignBox({ templates, onSent }) {
    const [selectedTpl, setSelectedTpl] = useState("");
    const [target, setTarget] = useState("no_clientes");
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    async function send() {
        if (!selectedTpl) { setError("Selecciona una plantilla"); return; }
        const tplName = templates.find(t => t.id === selectedTpl)?.subject || selectedTpl;
        const targetLabel = { no_clientes: "no clientes ni descartados", sin_contactar: "solo sin contactar", todos: "TODOS" }[target];
        if (!confirm(`Vas a enviar la plantilla "${tplName}" a ${targetLabel}. ¿Confirmas el envío masivo?`)) return;
        setSending(true); setError(""); setResult(null);
        try {
            const { data } = await api.post("/admin/prospectos/bulk-send", { template_id: selectedTpl, target });
            setResult(data);
            if (onSent) onSent();
        } catch (e) {
            setError(formatApiError(e, "Error en envío masivo"));
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="gf-card border-l-4 border-l-[var(--gf-warning)]" data-testid="bulk-campaign-box">
            <div className="flex items-start gap-3 mb-4">
                <Lightning size={24} weight="fill" className="text-[var(--gf-warning)] flex-shrink-0 mt-1" />
                <div>
                    <div className="micro-label">— Envío masivo a prospectos</div>
                    <h2 className="serif text-2xl mt-1">Campaña puntual</h2>
                    <p className="text-sm text-[var(--gf-text-secondary)] mt-2 max-w-2xl leading-relaxed">
                        Envía cualquier plantilla a tu cartera CRM de prospectos. Ideal para la <strong>campaña Sociedades 2025</strong>
                        (plantilla específica ya creada abajo) u otras acciones puntuales. Los que ya sean cliente o estén descartados se excluyen por defecto.
                    </p>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3 mb-4">
                <div className="md:col-span-2">
                    <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Plantilla a enviar</label>
                    <select value={selectedTpl} onChange={(e) => setSelectedTpl(e.target.value)} className="gf-input text-sm" data-testid="bulk-template-select">
                        <option value="">— Selecciona plantilla —</option>
                        {templates.filter(t => t.active).map(t => (
                            <option key={t.id} value={t.id}>{t.subject}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="text-[11px] tracking-wider uppercase text-[var(--gf-text-secondary)] mb-1 block">Destinatarios</label>
                    <select value={target} onChange={(e) => setTarget(e.target.value)} className="gf-input text-sm" data-testid="bulk-target-select">
                        <option value="no_clientes">Excluir clientes y descartados</option>
                        <option value="sin_contactar">Solo "Sin contactar"</option>
                        <option value="todos">TODOS (incluso clientes)</option>
                    </select>
                </div>
            </div>

            {error && <div className="text-sm text-[var(--gf-danger)] mb-3">{error}</div>}

            {result && (
                <div className="border-l-2 border-[var(--gf-success)] bg-green-50/60 pl-4 py-2 text-sm mb-3" data-testid="bulk-result">
                    Campaña <strong>"{result.plantilla}"</strong> enviada · <strong>{result.enviados}</strong> emails enviados · {result.fallidos} fallidos · {result.total_objetivo} prospectos en el objetivo. Los prospectos "sin contactar" se han movido automáticamente a "contactado".
                </div>
            )}

            <button onClick={send} disabled={sending || !selectedTpl} className="btn-gold text-sm" data-testid="bulk-send-btn">
                <PaperPlaneTilt size={14} weight="fill" /> {sending ? "Enviando…" : "Enviar campaña ahora"}
            </button>
        </div>
    );
}

/* ============================================================ */
/*  AsesoriasTab — vista dedicada para leads tipo=asesoria + clientes is_asesoria */
/* ============================================================ */
function AsesoriasTab({ leads, users, onWhatsApp, templates, onSendManual, sendingManual, onSendCampaign }) {
    const leadsAsesoria = (leads || []).filter((l) => l.tipo_cliente === "asesoria");
    const usuariosAsesoria = (users || []).filter((u) => u.is_asesoria);

    function exportCSV() {
        if (leadsAsesoria.length === 0) return;
        const rows = [
            ["fecha", "email", "telefono", "nombre", "empresa", "veredicto", "rojos", "ambars", "ip"],
            ...leadsAsesoria.map((l) => [
                l.created_at, l.email, l.telefono || "", l.nombre || "", l.nombre_empresa || "",
                l.veredicto || "", l.rojos || 0, l.ambars || 0, l.ip || "",
            ]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `asesorias-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <div className="space-y-6 mb-10">
            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-4">
                <StatCard label="Leads asesorías" value={leadsAsesoria.length} hint="Han probado La Máquina" testId="stat-leads-ases" />
                <StatCard label="Clientes asesorías" value={usuariosAsesoria.length} hint="Han comprado un Pack" testId="stat-clientes-ases" />
                <StatCard label="Conversión" value={leadsAsesoria.length ? `${Math.round((usuariosAsesoria.length / leadsAsesoria.length) * 100)}%` : "—"} hint="leads → clientes" testId="stat-conv-ases" />
            </div>

            {/* Acciones */}
            <div className="gf-card border-l-4 border-l-[var(--gf-gold)]" data-testid="asesorias-actions">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <Buildings size={28} weight="light" className="text-[var(--gf-gold)] mt-1 flex-shrink-0" />
                        <div>
                            <div className="micro-label mb-1">— Outreach B2B</div>
                            <h2 className="serif text-2xl">Plan de contacto con asesorías</h2>
                            <p className="text-xs text-[var(--gf-text-secondary)] mt-1 max-w-2xl leading-relaxed">
                                Los leads marcados como <strong>"Asesoría / Gestoría"</strong> son tu prioridad — una asesoría puede generar decenas de diagnósticos al año (uno por cada cliente que va al banco). Llámales personalmente por WhatsApp con el
                                script preparado.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <button onClick={onSendCampaign} className="btn-gold whitespace-nowrap" data-testid="send-outreach-asesorias">
                            <PaperPlaneTilt size={14} weight="fill" /> Enviar campaña a asesorías
                        </button>
                        <button onClick={exportCSV} disabled={leadsAsesoria.length === 0} className="btn-outline whitespace-nowrap" data-testid="export-asesorias-csv">
                            <Download size={14} weight="bold" /> Exportar leads CSV
                        </button>
                    </div>
                </div>
            </div>

            {/* Script outreach */}
            <details className="gf-card cursor-pointer" data-testid="script-outreach">
                <summary className="font-semibold text-sm flex items-center gap-2">
                    <FileText size={16} weight="light" /> Ver script de WhatsApp para asesorías (cópialo)
                </summary>
                <div className="mt-4 text-sm text-[var(--gf-text-secondary)] leading-relaxed bg-[var(--gf-subtle)] p-4 border-l-2 border-l-[var(--gf-gold)]">
                    <p className="mb-3">
                        <strong>Hola [Nombre],</strong> te escribo desde <strong>Gestión Fiscal 347</strong>. He visto que tu asesoría podría estar perdiendo clientes cuando van al banco a pedir financiación y vuelven con un "no" sin explicación.
                    </p>
                    <p className="mb-3">
                        Trabajamos con asesorías y gestorías preparando <strong>expedientes pre-bancarios</strong> — el informe que tu cliente necesita cuando va a financiación y donde un analista detecta en segundos lo que un balance "limpio" oculta (ratios, simetría activo-pasivo, capacidad de devolución…).
                    </p>
                    <p className="mb-3">
                        Cada Diagnóstico Pre-Bancario te cuesta <strong>19€ + IVA (22,99€)</strong> — pago único, sin packs ni suscripciones. Lo firmas con tu logo y lo facturas a tu cliente al precio que decidas (60, 90, 120€…). Es servicio profesional, no commodity.
                    </p>
                    <p className="font-semibold">
                        ¿Te llamo 10 minutos esta semana? Más de 30 años en banca empresarial española.<br />
                        👉 gf347.es
                    </p>
                </div>
            </details>

            {/* Tabla de leads asesoría */}
            <div className="gf-card">
                <h3 className="serif text-xl mb-4">Leads de asesorías ({leadsAsesoria.length})</h3>
                {leadsAsesoria.length === 0 ? (
                    <div className="text-center py-10 text-[var(--gf-text-muted)]" data-testid="no-asesorias-leads">
                        <Buildings size={40} weight="light" className="mx-auto mb-3 text-[var(--gf-border)]" />
                        <div className="text-sm">Aún no hay leads marcados como Asesoría / Gestoría.</div>
                        <div className="text-xs mt-1">Aparecerán aquí cuando alguien marque "Asesoría / Gestoría" en La Máquina.</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="data-table" data-testid="asesorias-leads-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Contacto</th>
                                    <th>Empresa</th>
                                    <th>Veredicto</th>
                                    <th className="text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leadsAsesoria.map((l) => (
                                    <tr key={l.id}>
                                        <td className="text-sm text-[var(--gf-text-secondary)]">
                                            {new Date(l.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                        </td>
                                        <td className="text-sm">
                                            <a href={`mailto:${l.email}`} className="hover:text-[var(--gf-gold)] underline-offset-4 hover:underline">{l.email}</a>
                                            {l.telefono && <div className="text-[11px] text-[var(--gf-text-muted)] mono mt-0.5">{l.telefono}</div>}
                                            {l.upgrade_clicked_at && (
                                                <div className="inline-block mt-1 px-1.5 py-0.5 bg-[var(--gf-gold)]/15 text-[var(--gf-gold)] text-[9px] tracking-widest uppercase font-semibold" title={`Pulsó CTA el ${new Date(l.upgrade_clicked_at).toLocaleString("es-ES")}`}>
                                                    🔥 Pulsó CTA
                                                </div>
                                            )}
                                            {l.nombre && <div className="text-[11px] text-[var(--gf-text-secondary)] mt-0.5">{l.nombre}</div>}
                                        </td>
                                        <td className="text-sm text-[var(--gf-text-secondary)]">{l.nombre_empresa || "—"}</td>
                                        <td>
                                            {l.veredicto && (
                                                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold ${l.veredicto === "verde" ? "bg-green-50 text-[var(--gf-success)]" : l.veredicto === "ambar" ? "bg-amber-50 text-[var(--gf-warning)]" : "bg-red-50 text-[var(--gf-danger)]"}`}>
                                                    {l.veredicto}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => onWhatsApp(l)}
                                                    disabled={!l.telefono}
                                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#25D366] text-white hover:bg-[#1ea953] disabled:opacity-30 disabled:cursor-not-allowed"
                                                    data-testid={`ases-wa-${l.id}`}
                                                >
                                                    <WhatsappLogo size={12} weight="bold" /> WhatsApp
                                                </button>
                                                {!l.unsubscribed && (
                                                    <select
                                                        onChange={(e) => { if (e.target.value) { onSendManual(l, e.target.value); e.target.value = ""; } }}
                                                        disabled={sendingManual === l.id}
                                                        defaultValue=""
                                                        className="text-xs border border-[var(--gf-border)] bg-white px-2 py-1"
                                                        data-testid={`ases-send-${l.id}`}
                                                    >
                                                        <option value="" disabled>{sendingManual === l.id ? "…" : "Email"}</option>
                                                        {(templates || []).filter(t => t.active).map(t => (
                                                            <option key={t.id} value={t.id}>{t.subject.slice(0, 40)}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Tabla de clientes asesoría activos */}
            {usuariosAsesoria.length > 0 && (
                <div className="gf-card">
                    <h3 className="serif text-xl mb-4">Clientes asesorías activos ({usuariosAsesoria.length})</h3>
                    <div className="overflow-x-auto">
                        <table className="data-table" data-testid="asesorias-users-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Nombre</th>
                                    <th className="text-right">Créditos restantes</th>
                                    <th>Alta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuariosAsesoria.map((u) => (
                                    <tr key={u.id}>
                                        <td className="text-sm">{u.email}</td>
                                        <td className="text-sm text-[var(--gf-text-secondary)]">{u.name || "—"}</td>
                                        <td className="text-right mono font-semibold">{formatEUR(u.credits_eur || 0)}</td>
                                        <td className="text-sm text-[var(--gf-text-secondary)]">{u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, hint, testId }) {
    return (
        <div className="gf-card-ficha" data-testid={testId}>
            <div className="micro-label mb-2">{label}</div>
            <div className="editorial-num">{value}</div>
            {hint && <div className="text-xs text-[var(--gf-text-muted)] mt-2">{hint}</div>}
        </div>
    );
}
